import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Chip, Tooltip, Snackbar, Menu, MenuItem, Divider, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useMutation } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useNavigate } from 'react-router-dom';
import OrganizationSelector from './OrganizationSelector';
import apiClient from '../../api/client';

export const Header: React.FC = () => {
  const { logout, isSuperAdmin, user } = useAuth();
  const { selectedOrgId, setSelectedOrgId } = useOrganization();
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSnackbar, setPasswordSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  const handleCopyOrgId = async () => {
    const orgIdToCopy = isSuperAdmin ? selectedOrgId : user?.organizationId;
    if (orgIdToCopy) {
      try {
        await navigator.clipboard.writeText(orgIdToCopy);
        setCopySuccess(true);
      } catch (err) {
        console.error('Не удалось скопировать ID организации:', err);
      }
    }
  };
  
  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  const handleOpenAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleCloseAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const openPasswordDialog = () => {
    setPasswordDialogOpen(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
  };

  const changePasswordMutation = useMutation(
    (payload: { currentPassword: string; newPassword: string }) =>
      apiClient.post('/api/auth/change-password', payload),
    {
      onSuccess: () => {
        setPasswordSnackbar({ open: true, message: 'Пароль успешно изменен', severity: 'success' });
        setPasswordDialogOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 'Не удалось сменить пароль';
        setPasswordSnackbar({ open: true, message, severity: 'error' });
      }
    }
  );

  const handlePasswordSubmit = () => {
    if (newPassword !== confirmPassword) {
      setPasswordSnackbar({ open: true, message: 'Пароли не совпадают', severity: 'error' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordSnackbar({ open: true, message: 'Пароль должен содержать минимум 8 символов', severity: 'error' });
      return;
    }

    const requiresCurrentPassword = !(user?.role === 'operator' && user?.mustChangePassword);
    if (requiresCurrentPassword && !currentPassword) {
      setPasswordSnackbar({ open: true, message: 'Укажите текущий пароль', severity: 'error' });
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: requiresCurrentPassword ? currentPassword : '',
      newPassword
    });
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <IconButton edge="start" color="inherit" aria-label="menu">
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          ELEKTROM - Админ панель
        </Typography>
        
        {isSuperAdmin && (
          <Box sx={{ mr: 2 }}>
            <OrganizationSelector
              selectedOrgId={selectedOrgId}
              onOrgChange={setSelectedOrgId}
              showAllOption={true}
            />
          </Box>
        )}
        
        {/* Отображение ID организации */}
        {(isSuperAdmin ? selectedOrgId : user?.organizationId) && (
          <Box sx={{ mr: 2 }}>
            <Tooltip title="Нажмите чтобы скопировать ID организации для виджета оператора">
              <Chip
                label={`ID: ${(isSuperAdmin ? selectedOrgId : user?.organizationId)?.slice(0, 8)}...`}
                variant="outlined"
                onClick={handleCopyOrgId}
                icon={<ContentCopyIcon />}
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              />
            </Tooltip>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="inherit" onClick={handleOpenAccountMenu}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout} aria-label="logout">
            <LogoutIcon />
          </IconButton>
        </Box>
      </Toolbar>

      <Menu anchorEl={accountMenuAnchor} open={Boolean(accountMenuAnchor)} onClose={handleCloseAccountMenu}>
        <MenuItem
          onClick={() => {
            handleCloseAccountMenu();
            openPasswordDialog();
          }}
        >
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Сменить пароль" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleCloseAccountMenu();
            handleLogout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Выйти" />
        </MenuItem>
      </Menu>

      {/* Уведомление об успешном копировании */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message="ID организации скопирован в буфер обмена"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Dialog open={passwordDialogOpen} onClose={closePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Смена пароля</DialogTitle>
        <DialogContent dividers>
          {!(user?.role === 'operator' && user?.mustChangePassword) && (
            <TextField
              margin="dense"
              label="Текущий пароль"
              type="password"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          )}
          <TextField
            margin="dense"
            label="Новый пароль"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            helperText="Минимум 8 символов"
          />
          <TextField
            margin="dense"
            label="Подтвердите пароль"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handlePasswordSubmit}
            disabled={changePasswordMutation.isLoading}
          >
            {changePasswordMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={passwordSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPasswordSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPasswordSnackbar((prev) => ({ ...prev, open: false }))}
          severity={passwordSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {passwordSnackbar.message}
        </Alert>
      </Snackbar>
    </AppBar>
  );
};

